import {
  CircleAlert, File, FileArchive, FileAudio, FileBraces, FileCode, FileImage, FileSpreadsheet, FileText, FileVideo, RotateCw, X,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { Progress } from '@/components/shadcn/progress'
import { cn } from '@/lib/cn'
import type { Attachment } from './attachments'

const CODE = /\.(ts|tsx|js|jsx|mjs|py|rs|go|rb|java|kt|swift|c|cc|cpp|h|hpp|cs|php|sh|sql|html|css|scss|vue|svelte|toml|ya?ml)$/i

/** An icon for the kind of file, never a preview: the tray stays light however big the file is. */
function fileIcon(f: File): { icon: ReactNode; tone: string } {
  const n = f.name.toLowerCase()
  const t = f.type
  const s = 22
  if (t.startsWith('image/')) return { icon: <FileImage size={s} />, tone: 'text-info' }
  if (t.startsWith('video/')) return { icon: <FileVideo size={s} />, tone: 'text-merged' }
  if (t.startsWith('audio/')) return { icon: <FileAudio size={s} />, tone: 'text-merged' }
  if (t === 'application/pdf' || n.endsWith('.pdf')) return { icon: <FileText size={s} />, tone: 'text-err' }
  if (/\.(csv|tsv|xlsx?|ods|numbers)$/.test(n)) return { icon: <FileSpreadsheet size={s} />, tone: 'text-ok' }
  if (/\.(zip|tar|gz|tgz|bz2|xz|7z|rar)$/.test(n)) return { icon: <FileArchive size={s} />, tone: 'text-warn' }
  if (n.endsWith('.json')) return { icon: <FileBraces size={s} />, tone: 'text-warn' }
  if (CODE.test(n)) return { icon: <FileCode size={s} />, tone: 'text-accent-hover' }
  if (t.startsWith('text/') || /\.(md|txt|docx?|rtf|odt|pages|pptx?|key)$/.test(n)) return { icon: <FileText size={s} />, tone: 'text-fg-muted' }
  return { icon: <File size={s} />, tone: 'text-fg-subtle' }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

const ext = (name: string) => (name.includes('.') ? name.split('.').pop()!.slice(0, 5).toUpperCase() : 'FILE')

/**
 * The files attached to the message, side by side above where you type; each
 * one a square with a cross. They wrap, and past one row the tray scrolls.
 * Hover shows a preview, a click opens it large; both read the file from the
 * browser, so they work before (and while) it uploads.
 */
export function AttachmentTray({ items, onRemove, onRetry }: { items: Attachment[]; onRemove: (id: string) => void; onRetry: (id: string) => void }) {
  const [open, setOpen] = useState<Attachment | null>(null)
  if (!items.length) return null
  return (
    <>
      <div className="flex max-h-[94px] flex-wrap gap-2 overflow-y-auto px-3 pt-2" aria-label="Attached files">
        {items.map((a) => <Tile key={a.id} a={a} onOpen={() => setOpen(a)} onRemove={() => onRemove(a.id)} onRetry={() => onRetry(a.id)} />)}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => { if (!o) setOpen(null) }}>
        {open && (
          <DialogContent className="flex max-h-[88vh] flex-col gap-3 bg-surface-3 p-4 sm:max-w-4xl shadow-lg">
            <DialogHeader className="gap-0.5 pr-8">
              <DialogTitle className="truncate text-base">{open.file.name}</DialogTitle>
              <DialogDescription className="text-xs">{formatBytes(open.file.size)} · {statusOf(open)}</DialogDescription>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg border bg-bg">
              <FilePreview file={open.file} large />
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}

const statusOf = (a: Attachment) => a.status === 'error' ? a.error ?? 'Upload failed' : a.status === 'uploading' ? `Uploading · ${Math.round(a.progress * 100)}%` : `Uploaded to ${a.path}`

function Tile({ a, onOpen, onRemove, onRetry }: { a: Attachment; onOpen: () => void; onRemove: () => void; onRetry: () => void }) {
  const { icon, tone } = fileIcon(a.file)
  const err = a.status === 'error'
  const up = a.status === 'uploading'
  const pct = Math.round(a.progress * 100)
  return (
    <div className="group relative shrink-0 pr-1.5 pt-1.5">
      <HoverCard openDelay={250} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button type="button" onClick={err ? onRetry : onOpen} aria-label={`${a.file.name}, ${statusOf(a)}`}
            className={cn('relative flex size-[72px] flex-col items-center justify-center gap-1 overflow-hidden rounded-lg border bg-surface-3 px-1.5 text-center outline-none transition-colors animate-in fade-in-0 zoom-in-95 focus-visible:ring-[3px] focus-visible:ring-ring/40',
              err ? 'border-destructive/60 bg-destructive/10' : 'border-border-strong hover:border-ring/60')}>
            <span className={cn(err ? 'text-err' : tone, up && 'opacity-50')}>{err ? <CircleAlert size={22} /> : icon}</span>
            <span className="w-full truncate text-[10px] leading-tight text-fg-muted">{a.file.name}</span>
            <span className="text-[9px] font-medium leading-none tracking-wide text-fg-faint">
              {err ? <span className="flex items-center gap-0.5 text-err"><RotateCw size={8} />RETRY</span> : up ? <span className="tabular-nums text-accent-hover">{pct}%</span> : ext(a.file.name)}
            </span>
            {up && <Progress value={Math.max(3, pct)} className="absolute inset-x-0 bottom-0 h-1 w-auto rounded-none bg-surface-4" />}
          </button>
        </HoverCardTrigger>
        <HoverCardContent side="top" align="start" className="w-72 overflow-hidden p-0">
          <div className="flex h-44 items-center justify-center overflow-hidden border-b bg-bg"><FilePreview file={a.file} /></div>
          <div className="px-3 py-2">
            <div className="truncate text-xs font-medium">{a.file.name}</div>
            <div className="text-[11px] text-muted-foreground">{formatBytes(a.file.size)} · {statusOf(a)}</div>
            {up && <Progress value={pct} className="mt-1.5 h-1" />}
          </div>
        </HoverCardContent>
      </HoverCard>
      <button type="button" onClick={onRemove} aria-label={`Remove ${a.file.name}`} title="Remove"
        className="absolute right-0 top-0 flex size-5 items-center justify-center rounded-full border border-border-strong bg-surface-4 text-fg-muted shadow-sm transition-colors hover:bg-destructive hover:text-white">
        <X size={11} strokeWidth={2.5} />
      </button>
    </div>
  )
}

const TEXTY = /\.(txt|md|csv|tsv|json|ya?ml|toml|xml|log|env|ini|sql|sh|ts|tsx|js|jsx|mjs|py|rs|go|rb|java|kt|swift|c|cc|cpp|h|hpp|cs|php|html|css|scss|vue|svelte)$/i

/** The file itself, read in the browser: image, video, audio, PDF or the start of a text file; otherwise its icon. */
export function FilePreview({ file, large = false }: { file: File; large?: boolean }) {
  const [url, setUrl] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const t = file.type
  const kind = t.startsWith('image/') ? 'image' : t.startsWith('video/') ? 'video' : t.startsWith('audio/') ? 'audio'
    : t === 'application/pdf' || /\.pdf$/i.test(file.name) ? 'pdf'
    : t.startsWith('text/') || t === 'application/json' || TEXTY.test(file.name) ? 'text' : 'other'
  useEffect(() => {
    if (kind === 'text') { void file.slice(0, large ? 200_000 : 4_000).text().then(setText); return }
    if (kind === 'other') return
    const u = URL.createObjectURL(file)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file, kind, large])
  if (kind === 'image' && url) return <img src={url} alt={file.name} className={cn('object-contain', large ? 'max-h-[72vh] max-w-full' : 'size-full')} />
  if (kind === 'video' && url) return <video src={url} controls={large} muted autoPlay={!large} loop={!large} className={cn('object-contain', large ? 'max-h-[72vh] max-w-full' : 'size-full')} />
  if (kind === 'audio' && url) return <audio src={url} controls className="w-[90%]" />
  if (kind === 'pdf' && url) return <iframe src={url} title={file.name} className={cn('border-0 bg-white', large ? 'h-[72vh] w-full' : 'pointer-events-none size-full')} />
  if (kind === 'text' && text !== null) return <pre className={cn('size-full overflow-auto whitespace-pre-wrap break-words p-3 text-left font-mono text-fg-muted', large ? 'max-h-[72vh] text-xs' : 'text-[10px] leading-snug')}>{text || '(empty file)'}</pre>
  const { icon, tone } = fileIcon(file)
  return <div className={cn('flex flex-col items-center gap-2 p-6 text-xs text-muted-foreground', tone)}><span className="[&_svg]:size-10">{icon}</span><span className="text-muted-foreground">No preview for this kind of file</span></div>
}
